import './AddIntegrationPopup.css';
import { useEffect, useState } from 'react';
import { useNotification } from 'src/hooks/useNotification';
import AddButtonIcon from '../../../assets/img/add-button-icon.svg';
import { useLoader } from 'src/provider/LoaderProvider';
import { baseApi, useAddIntegrationMutation } from 'src/services';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { clsx } from 'clsx';
import { useParams } from 'react-router-dom';
import { AddIntegrationList } from 'src/components/Integrations/AddIntegrationPopup/AddIntegrationList';
import { AddIntegrationSettings } from 'src/components/Integrations/AddIntegrationPopup/AddIntegrationSettings';
import { setSelectedIntegration } from 'src/store/slices/integrationSlice';
import { useAppSelector, useAppDispatch } from 'src/store';
import { Button } from 'src/components/ui/button';
import type { Project } from 'src/services/projectsService';
import type { SelectedIntegration } from 'src/store/slices/integrationSlice';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';

export const AddIntegrationPopup = ({
  project,
  disabled = false,
}: {
  project: Pick<Project, 'name' | 'integrations'>;
  disabled?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const notification = useNotification();
  const dispatch = useAppDispatch();
  const { selectedIntegration } = useAppSelector((s) => s.integrationSlices);
  const { id } = useParams();
  const [addIntegration, { isError: isErrorAddIntegration, isLoading: isLoadingAddIntegration }] =
    useAddIntegrationMutation();
  const loader = useLoader();

  const setIntegration = (data: SelectedIntegration | null) => {
    dispatch(setSelectedIntegration(data ? { ...selectedIntegration, ...data } : null));
  };

  // `skip` means the form already created the integration (e.g. via "Connect with Zendesk").
  const createIntegration = (data: SelectedIntegration, skip = false) => {
    if (skip) {
      dispatch(baseApi.util.invalidateTags(['ProjectById']));
      return onOpenChange(false);
    }

    addIntegration({ id, body: data }).then((addIntegrationResult) => {
      if (!('error' in addIntegrationResult)) {
        notification.success(
          `Integration ${addIntegrationResult.data.integration_type} was added.`,
        );
        loader.hide();
        onOpenChange(false);
        dispatch(baseApi.util.invalidateTags(['ProjectById']));
      }
    });
  };

  useLoaderWhile(isLoadingAddIntegration);

  useEffect(() => {
    if (isErrorAddIntegration) {
      loader.hide();
    }
  }, [isErrorAddIntegration]);

  const onOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    !isOpen && setIntegration(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger
        disabled={disabled}
        asChild
        className={clsx(disabled && 'pointer-events-none opacity-50', 'cursor-pointer')}
      >
        <Button variant="pill-outline" size="pill" className="card-header-button">
          <span>Add integration</span>
          <img src={AddButtonIcon} />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{selectedIntegration ? 'Integration Settings' : 'Integrations'}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          {selectedIntegration ? (
            <AddIntegrationSettings
              onSubmit={createIntegration}
              onBackButton={() => setIntegration(null)}
            />
          ) : (
            <AddIntegrationList project={project} onClick={(data) => setIntegration(data)} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
